import { useState } from 'react';
import { getApiErrorMessage } from '@/utils/apiError';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createForgotPasswordSchema,
  ForgotPasswordFormValues,
} from '@/utils/validations/forgotPassword.schema';
import InputField from '@/components/common/InputField';
import PhoneInput, { PhoneData } from '@/components/common/PhoneInput';
import OtpTimer from '@/components/common/OtpTimer';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import { BiSolidLock } from 'react-icons/bi';
import { MdShield } from 'react-icons/md';
import { useForgotPasswordMutation, useResetPasswordMutation } from '@/store/api/authApi';
import { useOtpTimer } from '@/hooks/useOtpTimer';
import { ApiError } from '@/types/error';
import { Loader } from '@/components/common/Loader';
import { toast } from 'sonner';

const initialPhone: PhoneData = { countryCode: '', phoneNumber: '', country: '', fullNumber: '' };

const getErrorMessage = getApiErrorMessage;

const ForgotPasswordPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [phone, setPhone] = useState<PhoneData>(initialPhone);

  const { timeLeft, active, start, reset: resetTimer } = useOtpTimer(120);

  const [forgotPassword, { isLoading: otpLoading }] = useForgotPasswordMutation();
  const [resetPassword, { isLoading: isResetting, error: resetApiError }] =
    useResetPasswordMutation();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    trigger,
    resetField,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(createForgotPasswordSchema()),
    defaultValues: {
      phone: '',
      otp: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const handleResetOtpField = () => {
    setShowOtpField(false);
    resetTimer();
    resetField('otp');
  };

  const handleSendOtp = async () => {
    const isValid = await trigger('phone');
    if (!isValid) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    try {
      await forgotPassword({ mobile: phone.fullNumber }).unwrap();

      toast.success(`Reset code sent to ${phone.fullNumber}`);
      setShowOtpField(true);
      start();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reset code'));
    }
  };

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    try {
      await resetPassword({
        code: data.otp,
        newPassword: data.newPassword,
        mobile: phone.fullNumber,
      }).unwrap();

      toast.success('Password reset successfully. Please login.');
      navigate('/login', { replace: true });
    } catch (error) {
      // handled via resetApiError below
    }
  };

  const errorMessage =
    errors.phone?.message ||
    errors.otp?.message ||
    errors.newPassword?.message ||
    errors.confirmPassword?.message ||
    (resetApiError as ApiError | undefined)?.data?.message;

  return (
    <div
      className="flex flex-col relative h-full !bg-no-repeat !bg-cover !bg-center"
      style={{ background: `url('/images/login-bg.webp')` }}
    >
      {isResetting && (
        <Loader className={'h-[100dvh] flex items-center justify-center bg-black/50'} />
      )}
      <div>
        {/* Back Button */}
        <Link
          to={'/login'}
          className="ml-2.5 mt-2.5 inline-flex size-9 items-center justify-center rounded-full bg-white"
        >
          <i className={'icon-arrow-down text-lg rotate-90 text-black'} />
        </Link>

        {/* Logo */}
        <div className="flex justify-center px-8 pb-2 pt-12">
          <img src="/images/logo-9winbet-v2.webp" alt="Velki" className="w-[14.5rem]" />
          {/* <img src="/images/logo-black.webp" alt="Velki" className="w-[14.5rem]" /> */}
        </div>
      </div>

      {/* Form Card */}
      <div className="flex flex-col">
        {/* Banner */}
        {/* <div className="w-[96%] mx-auto">
          <img src="/images/login-banner.webp" alt="banner" className="w-full" />
        </div> */}

        <div className="py-7 bg-white rounded-t-[1rem]">
          <form onSubmit={handleSubmit(onSubmit)} className={'w-5/6 mx-auto'}>
            <h1 className="mb-2 text-center text-[1.875rem] font-normal">FORGOT PASSWORD</h1>
            <p className="mb-6 text-center text-[0.938rem] text-gray-500">
              Enter your mobile number to receive a reset code.
            </p>

            <div className="space-y-5">
              {/* Mobile + Send OTP */}
              <div>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field }) => (
                    <div className="flex gap-2">
                      <PhoneInput
                        value={field.value}
                        disabled={active}
                        defaultCountry="BD"
                        onChange={(data) => {
                          setPhone(data);
                          field.onChange(data.fullNumber);
                        }}
                      />

                      <button
                        type="button"
                        onClick={active ? handleResetOtpField : handleSendOtp}
                        disabled={otpLoading}
                        className="h-[3.125rem] shrink-0 rounded-lg bg-primary px-3 text-[0.875rem] font-bold text-black disabled:opacity-60"
                      >
                        {otpLoading ? '...' : active ? 'Change?' : 'Send OTP'}
                      </button>
                    </div>
                  )}
                />
              </div>

              {showOtpField && (
                <>
                  <div>
                    <div className="mb-1 flex justify-end">
                      <OtpTimer timeLeft={timeLeft} active={active} onResend={handleSendOtp} />
                    </div>
                    <InputField
                      control={control}
                      name="otp"
                      label="OTP"
                      type="text"
                      startIcon={<MdShield className={'text-[1.8rem]'} />}
                    />
                  </div>

                  <InputField
                    control={control}
                    name="newPassword"
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    startIcon={<BiSolidLock className={'text-[1.8rem]'} />}
                    endIcon={
                      <button
                        className="text-[1.625rem]"
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? <IoMdEye /> : <IoMdEyeOff />}
                      </button>
                    }
                  />

                  <InputField
                    control={control}
                    name="confirmPassword"
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    startIcon={<BiSolidLock className={'text-[1.8rem]'} />}
                    endIcon={
                      <button
                        className="text-[1.625rem]"
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                      >
                        {showConfirmPassword ? <IoMdEye /> : <IoMdEyeOff />}
                      </button>
                    }
                  />
                </>
              )}

              {errorMessage && <p className="text-red-600 text-[0.938rem]">{errorMessage}</p>}

              <button
                type="submit"
                disabled={isResetting || !showOtpField}
                className="bg-primary p-3 rounded flex justify-center items-center disabled:bg-black-300 disabled:text-black-600 disabled:cursor-not-allowed w-52 h-[3.125rem] mx-auto text-[1.063rem] font-extrabold"
              >
                Reset Password
              </button>

              <p className="text-center text-[0.938rem]">
                Remembered your password?{' '}
                <Link to="/login" className="font-bold underline">
                  Login
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
